import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * Modal Component - A flexible, accessible modal dialog component
 *
 * Features:
 * - Portal rendering (renders outside main component tree)
 * - Keyboard navigation (Escape to close)
 * - Backdrop click to close (optional)
 * - Multiple size variants
 * - Customizable close button placement
 * - Body scroll prevention when open
 * - Accessible design with proper focus management
 *
 * @param {Object} props - Component props
 * @param {boolean} is_open - Controls modal visibility
 * @param {function} on_close - Callback function when modal closes
 * @param {ReactNode} children - Modal content
 * @param {string} title - Modal title (optional)
 * @param {boolean} show_close_button - Show/hide close button (default: true)
 * @param {boolean} backdrop_close - Allow closing by clicking backdrop (default: true)
 * @param {boolean} floating_close - Float close button over content (default: false)
 * @param {boolean} hide_titlebar - Hide entire title bar (default: false)
 * @param {string} size - Modal size: "sm", "md", "lg", "xl", "full" (default: "md")
 * @param {string} className - Additional CSS classes for modal container
 *
 * Usage Examples:
 *
 * Basic Modal:
 * <Modal is_open={showModal} on_close={() => setShowModal(false)} title="My Modal">
 *   <p>Modal content goes here</p>
 * </Modal>
 *
 * Large Modal with Custom Styling:
 * <Modal
 *   is_open={showModal}
 *   on_close={() => setShowModal(false)}
 *   title="Large Modal"
 *   size="lg"
 *   className="border-2 border-blue-500"
 * >
 *   <div className="p-6">Large modal content</div>
 * </Modal>
 *
 * Floating Close Button (no title bar):
 * <Modal
 *   is_open={showModal}
 *   on_close={() => setShowModal(false)}
 *   floating_close={true}
 *   hide_titlebar={true}
 * >
 *   <img src="image.jpg" alt="Full screen image" />
 * </Modal>
 *
 * No Backdrop Close:
 * <Modal
 *   is_open={showModal}
 *   on_close={() => setShowModal(false)}
 *   title="Confirmation"
 *   backdrop_close={false}
 * >
 *   <p>Are you sure you want to delete this item?</p>
 *   <button onClick={() => setShowModal(false)}>Cancel</button>
 * </Modal>
 */
export const Modal = ({
  is_open,
  on_close,
  children,
  title = "",
  show_close_button = true,
  backdrop_close = true,
  floating_close = false,
  hide_titlebar = false,
  size = "md", // "sm", "md", "lg", "xl", "full"
  className = "",
}) => {
  // Close modal on Escape key press - improves accessibility
  useEffect(() => {
    const handle_escape = (e) => {
      if (e.key === "Escape" && is_open) {
        on_close();
      }
    };

    document.addEventListener("keydown", handle_escape);
    return () => document.removeEventListener("keydown", handle_escape);
  }, [is_open, on_close]);

  // Prevent body scroll when modal is open - keeps background content from scrolling
  useEffect(() => {
    if (is_open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [is_open]);

  // Don't render anything if modal is closed
  if (!is_open) return null;

  // Size variants - responsive modal widths
  const size_classes = {
    sm: "max-w-sm", // ~384px - Small dialogs, confirmations
    md: "max-w-2xl", // ~672px - Standard forms, content
    lg: "max-w-4xl", // ~896px - Large forms, detailed content
    xl: "max-w-6xl", // ~1152px - Very large content, data tables
    full: "max-w-[95vw] max-h-[95vh]", // Nearly full screen - images, galleries
  };

  const modal_content = (
    // Backdrop overlay - covers entire viewport with semi-transparent background
    <div
      className="fixed inset-0 bg-black/20 backdrop-blur-md z-[9999] flex items-center justify-center p-4"
      onClick={backdrop_close ? on_close : undefined} // Close on backdrop click if enabled
    >
      {/* Modal container - the actual modal box */}
      <div
        className={`bg-gray-900 rounded-xl border border-gray-800 w-full ${size_classes[size]} max-h-[90vh] overflow-hidden relative ${className}`}
        onClick={(e) => e.stopPropagation()} // Prevent backdrop close when clicking modal content
      >
        {/* Floating Close Button - positioned absolutely over content */}
        {floating_close && show_close_button && (
          <button
            onClick={on_close}
            className="absolute top-4 right-4 z-10 p-2 hover:bg-gray-800 rounded-lg transition-colors bg-gray-800/80 backdrop-blur-sm"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        )}

        {/* Modal Header - title bar with optional close button */}
        {!hide_titlebar && (title || show_close_button) && (
          <div className="flex items-center justify-between p-6 border-b border-gray-800">
            <h2 className="text-xl font-bold text-white">{title}</h2>
            {/* Standard close button in header - only show if not using floating close */}
            {show_close_button && !floating_close && (
              <button
                onClick={on_close}
                className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                aria-label="Close modal"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            )}
          </div>
        )}

        {/* Modal Body - scrollable content area */}
        <div
          style={{
            // hide_titlebar ? 'max-h-[90vh]' : 'max-h-[calc(90vh-120px)]'
            maxHeight: hide_titlebar ? "90vh" : "calc(90vh - 120px)",
          }}
          className={`overflow-y-auto`}
        >
          {children}
        </div>
      </div>
    </div>
  );

  // Render modal in a portal (outside main component tree)
  // This ensures modal appears above all other content regardless of z-index context
  return createPortal(modal_content, document.body);
};
